// Testimonial1.js - Contains functions for creating a testimonial slider section
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Testimonial section with slider functionality for testimonial cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The testimonial section elements
 */
export const createTestimonialElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const testimonialSectionClass = 'testimonial1-section';
  const testimonialContainerClass = 'testimonial1-container';
  const testimonialHeaderClass = 'testimonial1-header';
  const testimonialTitleClass = 'testimonial1-title';
  const testimonialSubtitleClass = 'testimonial1-subtitle';
  const testimonialSliderClass = 'testimonial1-slider';
  const testimonialSlideClass = 'testimonial1-slide';
  const testimonialCardClass = 'testimonial1-card';
  const testimonialControlsClass = 'testimonial1-controls';
  
  // Desktop Styles (default)
  const testimonialSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f9f9f9',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const testimonialContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '40px'
  };

  const testimonialHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const testimonialTitleStyles = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '16px'
  };

  const testimonialSubtitleStyles = {
    fontSize: '18px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '30px'
  };

  const testimonialSliderStyles = {
    position: 'relative',
    width: '100%',
    height: '400px',
    overflow: 'hidden',
    backgroundColor: 'transparent'
  };

  const testimonialSlideStyles = {
    flex: '0 0 100%', // Will be adjusted based on slidesPerView
    height: '100%',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    padding: '10px'
  };

  const testimonialCardStyles = {
    width: '100%',
    height: '100%',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 5px 20px rgba(0,0,0,0.08)',
    padding: '30px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#666',
    transition: 'all 0.3s ease'
  };

  const testimonialControlsStyles = {
    display: 'flex',
    gap: '15px',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '25px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(testimonialSectionClass, testimonialSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialContainerClass, testimonialContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialHeaderClass, testimonialHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialTitleClass, testimonialTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSubtitleClass, testimonialSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSliderClass, testimonialSliderStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialSlideClass, testimonialSlideStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialCardClass, testimonialCardStyles);
    newRegistry[DEVICES.DESKTOP].set(testimonialControlsClass, testimonialControlsStyles);

    // Add tablet and mobile styles if needed
    // For responsive considerations

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    testimonialSectionClass,
    testimonialContainerClass,
    testimonialHeaderClass,
    testimonialTitleClass,
    testimonialSubtitleClass,
    testimonialSliderClass,
    testimonialSlideClass,
    testimonialCardClass,
    testimonialControlsClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = sectionId + 1;
  
  // Header elements
  const headerId = sectionId + 2;
  const titleId = sectionId + 3;
  const subtitleId = sectionId + 4;
  
  // Slider elements
  const sliderId = sectionId + 5;
  const maskId = sliderId + 1;
  const leftArrowId = sliderId + 2;
  const rightArrowId = sliderId + 3;
  
  // Controls elements
  const controlsId = sliderId + 4;
  const autoplayToggleId = sliderId + 5;
  const loopToggleId = sliderId + 6;
  const speedContainerId = sliderId + 7;
  const speedInputId = sliderId + 8;
  
  // Slide IDs - increase the gaps to avoid ID collisions
  const slide1Id = sliderId + 100;
  const slide2Id = sliderId + 200;
  const slide3Id = sliderId + 300;
  const slide4Id = sliderId + 400;

  // Card IDs - explicitly separate from slide IDs
  const card1Id = slide1Id + 1000;
  const card2Id = slide2Id + 1000;
  const card3Id = slide3Id + 1000;
  const card4Id = slide4Id + 1000;

  // Array of testimonial data - Define this at the top level so it's available to all functions
  const testimonials = [
    {
      name: 'Sarah Johnson',
      position: 'Marketing Director',
      company: 'XYZ Company',
      quote: 'The website builder was incredibly intuitive and allowed us to create a professional site in record time. The customer support team was also very helpful when we needed assistance.',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      rating: 5
    },
    {
      name: 'Michael Chen',
      position: 'Startup Founder',
      company: 'Tech Innovators',
      quote: 'As a startup founder with limited resources, this platform was a game-changer. We were able to build an impressive online presence without breaking the bank.',
      avatar: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      rating: 4
    },
    {
      name: 'Emily Rodriguez',
      position: 'Small Business Owner',
      company: 'Artisan Crafts',
      quote: 'I had no technical experience but was able to create a beautiful website for my small business. The drag-and-drop interface made it so simple!',
      avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      rating: 5
    },
    {
      name: 'James Wilson',
      position: 'Digital Marketer',
      company: 'Growth Solutions',
      quote: 'The SEO tools built into this platform have helped our clients achieve significant improvements in their search rankings. Highly recommended!',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=150&q=80',
      rating: 5
    }
  ];
  
  console.log("Testimonials array:", testimonials);

  // Create a simple slide with a card child
  const createSlide = (slideId, cardId, index) => {
    return {
      id: slideId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Slide ${index}`,
      parentId: maskId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialSlideClass,
      sliderRole: 'slide',
      styles: {
        flex: '0 0 33.3333%',
        height: '100%'
      },
      text: '',
      children: [cardId], // Each slide has exactly ONE child - its card
      visibility: 'visible',
      isContainer: true,
      attributes: {
        'data-element': 'testimonial-slide',
        'data-slide-index': index - 1
      }
    };
  };

  // Create simple slides - with explicit card IDs
  const slide1 = createSlide(slide1Id, card1Id, 1);
  const slide2 = createSlide(slide2Id, card2Id, 2);
  const slide3 = createSlide(slide3Id, card3Id, 3);
  const slide4 = createSlide(slide4Id, card4Id, 4);
  
  // Create direct references to each testimonial to ensure correctness
  const sarahData = testimonials[0];  // Sarah Johnson
  const michaelData = testimonials[1]; // Michael Chen
  const emilyData = testimonials[2];  // Emily Rodriguez
  const jamesData = testimonials[3];  // James Wilson
  
  console.log("Sarah's data:", sarahData);
  console.log("Michael's data:", michaelData);
  console.log("Emily's data:", emilyData);
  console.log("James's data:", jamesData);
  
  // Create custom card creation function for fixed testimonials
  const createFixedCard = (id, parentId, testimonialData) => {
    console.log(`Creating fixed card with ID ${id} for ${testimonialData.name}`);
    
    // Generate unique offset for card's child elements
    const uniqueBase = id * 100;
    
    // Create IDs for child elements
    const ratingId = uniqueBase + 10;
    const quoteId = uniqueBase + 20;
    const authorContainerId = uniqueBase + 30;
    const avatarId = uniqueBase + 31;
    const authorInfoId = uniqueBase + 32;
    const nameId = uniqueBase + 33;
    const positionId = uniqueBase + 34;
    const companyId = uniqueBase + 35;
    
    // Create the main card element
    const cardElement = {
      id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: `Card with ${testimonialData.name}`,
      parentId: parentId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialCardClass,
      styles: {},
      text: '',
      children: [ratingId, quoteId, authorContainerId],
      visibility: 'visible',
      isContainer: true,
      attributes: {
        'data-element': 'testimonial-card',
        'data-testimonial-name': testimonialData.name
      }
    };
    
    // Create all child elements
    const childElements = [];
    
    // 1. Rating stars
    childElements.push({
      id: ratingId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Rating Stars',
      parentId: id, // This is the card's id
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        gap: '3px',
        marginBottom: '15px',
        color: '#FFD700',
        fontSize: '16px'
      },
      text: '★'.repeat(testimonialData.rating),
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-rating',
        'data-rating': testimonialData.rating
      }
    });
    
    // 2. Quote text
    childElements.push({
      id: quoteId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Quote',
      parentId: id, // This is the card's id
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '16px',
        lineHeight: '1.8',
        color: '#555555',
        marginBottom: '25px',
        fontStyle: 'italic',
        position: 'relative',
        paddingLeft: '10px',
        borderLeft: '3px solid #4361ee',
        width: '100%'
      },
      text: testimonialData.quote,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-quote'
      }
    });
    
    // 3. Author container
    childElements.push({
      id: authorContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Author Container',
      parentId: id, // This is the card's id
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        marginTop: 'auto',
        width: '100%'
      },
      text: '',
      children: [avatarId, authorInfoId],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-container'
      }
    });
    
    // 4. Avatar
    childElements.push({
      id: avatarId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Author Avatar',
      parentId: authorContainerId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        objectFit: 'cover',
        border: '3px solid #ffffff',
        boxShadow: '0 3px 10px rgba(0,0,0,0.1)'
      },
      src: testimonialData.avatar,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-avatar',
        'data-avatar-for': testimonialData.name,
        'alt': testimonialData.name
      }
    });
    
    // 5. Author info container
    childElements.push({
      id: authorInfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Author Info',
      parentId: authorContainerId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        flexDirection: 'column'
      },
      text: '',
      children: [nameId, positionId, companyId],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-info'
      }
    });
    
    // 6. Author name
    childElements.push({
      id: nameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h4',
      name: 'Author Name',
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('h4'),
      styles: {
        fontSize: '18px',
        fontWeight: 'bold',
        color: '#333333',
        marginBottom: '4px',
        marginTop: '0'
      },
      text: testimonialData.name,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-name'
      }
    });
    
    // 7. Author position
    childElements.push({
      id: positionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Author Position',
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#4361ee',
        marginBottom: '2px',
        marginTop: '0'
      },
      text: testimonialData.position,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-position'
      }
    });
    
    // 8. Author company
    childElements.push({
      id: companyId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Author Company',
      parentId: authorInfoId,
      hierarchyName: getHierarchyName('p'),
      styles: {
        fontSize: '14px',
        color: '#666666',
        marginTop: '0'
      },
      text: testimonialData.company,
      children: [],
      visibility: 'visible',
      attributes: {
        'data-element': 'testimonial-author-company'
      }
    });
    
    // Return card element and all its child elements
    return {
      card: cardElement,
      children: childElements
    };
  };
  
  // Create cards for each slide with explicitly specified testimonial data
  const card1Result = createFixedCard(card1Id, slide1Id, sarahData);
  const card2Result = createFixedCard(card2Id, slide2Id, michaelData);
  const card3Result = createFixedCard(card3Id, slide3Id, emilyData);
  const card4Result = createFixedCard(card4Id, slide4Id, jamesData);

  // Create slider arrow as link with image
  const createArrow = (id, direction) => {
    // Create unique ID for the image child
    const imageId = id + 1000; // Add 1000 to avoid ID collisions
    
    return {
      id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      sliderRole: 'arrow',
      direction: direction,
      text: '',
      styles: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '40px',
        height: '40px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        border: 'none',
        borderRadius: '50%',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        cursor: 'pointer',
        zIndex: 5,
        textDecoration: 'none',
        // Position left or right arrow accordingly
        left: direction === 'left' ? '10px' : 'auto',
        right: direction === 'right' ? '10px' : 'auto'
      },
      children: [imageId], // Reference to the image element
      parentId: sliderId,
      visibility: 'visible',
      hierarchyName: getHierarchyName('link'),
      interactions: {
        events: [
          {
            id: Date.now() + (direction === 'left' ? 1 : 2),
            type: 'click',
            action: 'custom',
            label: `${direction === 'left' ? 'Previous' : 'Next'} slide`,
            value: {
              script: `
                const slider = event.target.closest('[data-element-type="slider"]');
                if (!slider) return;
                
                const mask = slider.querySelector('[data-slider-role="mask"]');
                if (!mask) return;
                
                // Get device-specific settings based on current screen size
                let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 1);
                let slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || 1);
                
                // Get loop setting
                const loopEnabled = slider.getAttribute('data-loop') === 'true';
                
                // Choose breakpoint based on window width
                const windowWidth = window.innerWidth;
                let currentDevice = 'desktop';
                
                if (windowWidth <= 768) {
                  currentDevice = 'mobile';
                } else if (windowWidth <= 1024) {
                  currentDevice = 'tablet';
                }
                
                const slides = mask.children.length;
                let currentSlide = parseInt(mask.getAttribute('data-current-slide') || 0);
                
                if ('${direction}' === 'left') {
                  if (currentSlide === 0 && loopEnabled) {
                    // If at the beginning and loop is enabled, go to the end
                    currentSlide = slides - slidesPerView;
                  } else {
                    // Otherwise, navigate normally
                    currentSlide = Math.max(0, currentSlide - slidesPerScroll);
                  }
                } else {
                  if (currentSlide >= slides - slidesPerView && loopEnabled) {
                    // If at the end and loop is enabled, go to the beginning
                    currentSlide = 0;
                  } else {
                    // Otherwise, navigate normally
                    currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
                  }
                }
                
                // Apply transition for smooth movement
                mask.style.transition = 'transform 0.5s ease-in-out';
                mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
                mask.setAttribute('data-current-slide', currentSlide);
                
                event.preventDefault(); // Prevent default link behavior
              `
            }
          }
        ]
      },
      attributes: {
        'href': '#',
        'aria-label': direction === 'left' ? 'Previous slide' : 'Next slide',
        'data-direction': direction
      }
    };
  };

  // Create arrow image element
  const createArrowImage = (id, parentId, direction) => ({
    id,
    builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    type: 'image',
    text: '',
    styles: {
      width: '20px',
      height: '20px',
      objectFit: 'contain',
    },
    src: direction === 'left' 
      ? 'https://cdn-icons-png.flaticon.com/512/130/130882.png'  // Left arrow icon
      : 'https://cdn-icons-png.flaticon.com/512/130/130884.png', // Right arrow icon
    children: [],
    parentId: parentId,
    visibility: 'visible',
    hierarchyName: getHierarchyName('image')
  });
  
  // Create left and right arrows
  const leftArrow = createArrow(leftArrowId, 'left');
  const rightArrow = createArrow(rightArrowId, 'right');

  // Create left and right arrow image elements
  const leftArrowImage = createArrowImage(leftArrowId + 1000, leftArrowId, 'left');
  const rightArrowImage = createArrowImage(rightArrowId + 1000, rightArrowId, 'right');

  // First add all structural elements - the mask must be created before slides can reference it as parent
  const structuralElements = [
    // Main Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Testimonial Section',
      parentId: null,
      className: testimonialSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'testimonial-section',
        'data-component': 'section',
        'data-section-type': 'testimonial',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonial Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialContainerClass,
      styles: {},
      children: [headerId, sliderId, controlsId],
      text: '',
      attributes: {
        'data-element': 'testimonial-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Testimonial Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'testimonial-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Testimonial Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: testimonialTitleClass,
      styles: {},
      children: [],
      text: 'What Our Customers Say',
      attributes: {
        'data-element': 'testimonial-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Testimonial Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: testimonialSubtitleClass,
      styles: {},
      children: [],
      text: 'Don\'t just take our word for it. See what our satisfied customers have to say about their experience with us.',
      attributes: {
        'data-element': 'testimonial-subtitle'
      }
    },
    
    // Slider Container
    {
      id: sliderId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'slider',
      name: 'Testimonial Slider',
      parentId: containerId,
      hierarchyName: getHierarchyName('slider'),
      className: testimonialSliderClass,
      sliderRole: 'container',
      styles: {},
      children: [maskId, leftArrowId, rightArrowId],
      text: '',
      slidesPerView: 3, // Default for desktop
      slidesPerScroll: 1, // Default scrolling amount
      deviceSettings: {
        desktop: {
          slidesPerView: 3,
          slidesPerScroll: 1
        },
        tablet: {
          slidesPerView: 2,
          slidesPerScroll: 1
        },
        mobile: {
          slidesPerView: 1,
          slidesPerScroll: 1
        }
      },
      attributes: {
        'data-element': 'testimonial-slider',
        'data-element-type': 'slider',
        'data-loop': 'false',           // loop disabled by default
        'data-autoplay': 'false',       // autoplay disabled by default
        'data-delay': '5000'            // 5 seconds delay by default
      },
      // Initialize the slider with drag and autoplay functionality
      interactions: {
        events: [
          {
            id: Date.now() + 100,
            type: 'custom',
            action: 'custom',
            label: 'Initialize slider drag and autoplay functionality',
            value: {
              script: `
                // Initialize variables for drag functionality
                let isDragging = false;
                let startPosition = 0;
                let currentTranslate = 0;
                let prevTranslate = 0;
                let animationID = 0;
                let autoplayInterval = null;

                const slider = event.currentTarget;
                const mask = slider.querySelector('[data-slider-role="mask"]');
                if (!mask) return;

                // Add touch and mouse event listeners
                mask.addEventListener('mousedown', dragStart);
                mask.addEventListener('touchstart', dragStart, { passive: true });
                mask.addEventListener('mouseup', dragEnd);
                mask.addEventListener('touchend', dragEnd);
                mask.addEventListener('mouseleave', dragEnd);
                mask.addEventListener('mousemove', drag);
                mask.addEventListener('touchmove', drag, { passive: true });

                // Helper function to get current slide settings
                function getSliderSettings() {
                  return {
                    slidesPerView: parseInt(slider.getAttribute('data-slides-per-view') || '1'),
                    slidesPerScroll: parseInt(slider.getAttribute('data-slides-per-scroll') || '1'),
                    loopEnabled: slider.getAttribute('data-loop') === 'true',
                    autoplayEnabled: slider.getAttribute('data-autoplay') === 'true',
                    autoplayDelay: parseInt(slider.getAttribute('data-delay') || '5000')
                  };
                }

                // Function to handle drag start
                function dragStart(e) {
                  e.preventDefault();
                  if (e.type === 'touchstart') {
                    startPosition = e.touches[0].clientX;
                  } else {
                    startPosition = e.clientX;
                  }
                  
                  // Stop autoplay during drag
                  if (autoplayInterval) {
                    clearInterval(autoplayInterval);
                  }
                  
                  isDragging = true;
                  
                  // Get current transform value
                  const style = window.getComputedStyle(mask);
                  const matrix = new WebKitCSSMatrix(style.transform);
                  prevTranslate = matrix.m41; // Get the X translation value
                  
                  cancelAnimationFrame(animationID);
                }

                // Function to handle dragging
                function drag(e) {
                  if (!isDragging) return;
                  
                  let currentPosition;
                  if (e.type === 'touchmove') {
                    currentPosition = e.touches[0].clientX;
                  } else {
                    currentPosition = e.clientX;
                  }
                  
                  // Calculate drag distance
                  const diff = currentPosition - startPosition;
                  
                  // Calculate what the new position would be
                  currentTranslate = prevTranslate + diff;
                  
                  // Apply the transform without transition for smooth dragging
                  mask.style.transition = 'none';
                  mask.style.transform = \`translateX(\${currentTranslate}px)\`;
                }

                // Function to handle drag end with snap-to-slide
                function dragEnd() {
                  if (!isDragging) return;
                  
                  isDragging = false;
                  
                  // Re-enable transition for smooth snap
                  mask.style.transition = 'transform 0.3s ease-out';
                  
                  const settings = getSliderSettings();
                  const { slidesPerView, slidesPerScroll, loopEnabled } = settings;
                  const slides = mask.children.length;
                  
                  // Calculate the width of a single slide
                  const maskWidth = mask.offsetWidth;
                  const slideWidth = maskWidth / slidesPerView;
                  
                  // Calculate the slide to snap to based on current position
                  let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
                  
                  // Calculate movement threshold - if moved more than 20% of slide width
                  const moveThreshold = slideWidth * 0.2;
                  const movement = Math.abs(currentTranslate - prevTranslate);
                  
                  if (movement > moveThreshold) {
                    // Determine direction of swipe
                    if (currentTranslate > prevTranslate) {
                      // Swiped right (previous slide)
                      if (currentSlide === 0 && loopEnabled) {
                        currentSlide = slides - slidesPerView;
                      } else {
                        currentSlide = Math.max(0, currentSlide - slidesPerScroll);
                      }
                    } else {
                      // Swiped left (next slide)
                      if (currentSlide >= slides - slidesPerView && loopEnabled) {
                        currentSlide = 0;
                      } else {
                        currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
                      }
                    }
                  }
                  
                  // Move to the slide position
                  goToSlide(currentSlide);
                  
                  // Restart autoplay if enabled
                  initAutoplay();
                }

                // Function to go to a specific slide
                function goToSlide(slideIndex) {
                  const settings = getSliderSettings();
                  const { slidesPerView } = settings;
                  
                  mask.style.transform = \`translateX(-\${slideIndex * (100 / slidesPerView)}%)\`;
                  mask.setAttribute('data-current-slide', slideIndex);
                }

                // Function to handle autoplay
                function initAutoplay() {
                  const settings = getSliderSettings();
                  const { autoplayEnabled, autoplayDelay, loopEnabled } = settings;
                  
                  if (autoplayInterval) {
                    clearInterval(autoplayInterval);
                    autoplayInterval = null;
                  }
                  
                  if (autoplayEnabled) {
                    autoplayInterval = setInterval(() => {
                      const slides = mask.children.length;
                      const slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || '1');
                      const slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || '1');
                      let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
                      
                      // Calculate the next slide
                      if (currentSlide >= slides - slidesPerView) {
                        if (loopEnabled) {
                          currentSlide = 0;
                        } else {
                          // Stop autoplay once reached the end if not looping
                          clearInterval(autoplayInterval);
                          return;
                        }
                      } else {
                        currentSlide += slidesPerScroll;
                      }
                      
                      // Move to the next slide
                      goToSlide(currentSlide);
                    }, autoplayDelay);
                  }
                }

                // Initialize autoplay if enabled
                initAutoplay();

                // Re-initialize on window resize
                window.addEventListener('resize', () => {
                  dragEnd();
                  setTimeout(initAutoplay, 100);
                });
              `
            }
          }
        ]
      }
    },
    
    // Slider Mask
    {
      id: maskId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Slider Mask',
      parentId: sliderId,
      hierarchyName: getHierarchyName('div'),
      sliderRole: 'mask',
      styles: {
        position: 'absolute',
        display: 'flex',
        width: '100%',
        height: '100%',
        transition: 'transform 0.5s ease-in-out',
        cursor: 'grab'
      },
      children: [slide1Id, slide2Id, slide3Id, slide4Id],
      text: '',
      attributes: {
        'data-element': 'slider-mask',
        'data-current-slide': '0',
        'data-slides-count': '4'
      }
    },
    
    // Left slider arrow
    leftArrow,
    
    // Right slider arrow
    rightArrow,
    
    // Controls container
    {
      id: controlsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Slider Controls',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: testimonialControlsClass,
      styles: {},
      children: [autoplayToggleId, loopToggleId, speedContainerId],
      attributes: {
        'data-element': 'testimonial-controls'
      }
    },
    
    // Autoplay toggle
    {
      id: autoplayToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Autoplay Toggle',
      parentId: controlsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '',
      attributes: {
        'data-element': 'autoplay-toggle'
      },
      innerHTML: `
        <label for="autoplay-toggle-${sectionId}" style="font-size: 14px; color: #333;">Autoplay</label>
        <input type="checkbox" id="autoplay-toggle-${sectionId}" style="cursor: pointer;" onchange="
          const slider = document.querySelector('#${sliderId}');
          if (slider) {
            slider.setAttribute('data-autoplay', this.checked ? 'true' : 'false');
            
            // Dispatch a custom event to notify the slider to update autoplay
            const event = new CustomEvent('autoplayToggle', { detail: { enabled: this.checked } });
            slider.dispatchEvent(event);
            
            // Initialize autoplay based on new setting
            if (this.checked) {
              const delay = parseInt(slider.getAttribute('data-delay') || '5000');
              const mask = slider.querySelector('[data-slider-role=\\'mask\\']');
              if (mask) {
                let autoplayInterval = window[slider.id + '_autoplay'];
                if (autoplayInterval) {
                  clearInterval(autoplayInterval);
                }
                
                autoplayInterval = setInterval(() => {
                  const slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || '1');
                  const slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || '1');
                  const loopEnabled = slider.getAttribute('data-loop') === 'true';
                  const slides = mask.children.length;
                  let currentSlide = parseInt(mask.getAttribute('data-current-slide') || '0');
                  
                  if (currentSlide >= slides - slidesPerView) {
                    if (loopEnabled) {
                      currentSlide = 0;
                    } else {
                      clearInterval(autoplayInterval);
                      return;
                    }
                  } else {
                    currentSlide += slidesPerScroll;
                  }
                  
                  mask.style.transform = \`translateX(-\${currentSlide * (100/slidesPerView)}%)\`;
                  mask.setAttribute('data-current-slide', currentSlide);
                }, delay);
                
                window[slider.id + '_autoplay'] = autoplayInterval;
              }
            } else {
              let autoplayInterval = window[slider.id + '_autoplay'];
              if (autoplayInterval) {
                clearInterval(autoplayInterval);
                window[slider.id + '_autoplay'] = null;
              }
            }
          }
        ">
      `
    },
    
    // Loop toggle
    {
      id: loopToggleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Loop Toggle',
      parentId: controlsId,
      hierarchyName: getHierarchyName('div'),
      styles: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      },
      children: [],
      text: '',
      attributes: {
        'data-element': 'loop-toggle'
      },
      innerHTML: `
        <label for="loop-toggle-${sectionId}" style="font-size: 14px; color: #333;">Loop Slides</label>
        <input type="checkbox" id="loop-toggle-${sectionId}" style="cursor: pointer;" onchange="
          const slider = document.querySelector('#${sliderId}');
          if (slider) {
            slider.setAttribute('data-loop', this.checked ? 'true' : 'false');
          }
        ">
      `
    },
  ];
  
  // First clear out the array, then add in specific order:
  const newElements = []; 
  
  // First add all structural elements EXCEPT slides
  structuralElements.forEach(element => {
    // Skip slides for now, we'll add them separately
    if (element.sliderRole !== 'slide') {
      newElements.push(element);
    }
  });
  
  // Now add slides to elements array - directly as children of mask
  newElements.push(slide1);
  newElements.push(slide2);
  newElements.push(slide3);
  newElements.push(slide4);
  
  // Add cards to elements array
  newElements.push(card1Result.card);
  newElements.push(card2Result.card);
  newElements.push(card3Result.card);
  newElements.push(card4Result.card);
  
  // Add arrow image elements
  newElements.push(leftArrowImage);
  newElements.push(rightArrowImage);
  
  // Add card child elements for each card
  card1Result.children.forEach(element => newElements.push(element));
  card2Result.children.forEach(element => newElements.push(element));
  card3Result.children.forEach(element => newElements.push(element));
  card4Result.children.forEach(element => newElements.push(element));

  console.log("newElements", newElements);
  
  return {
    testimonialSection: newElements[0],
    elements: newElements
  };
};

export default createTestimonialElements; 